/**
 * components/hire/bulkUploadModal.tsx
 * Bulk import — drop many PDF/CSV/Excel files and create Jobs OR Candidates in
 * one pass. Deliberately two-stage, so nothing reaches the Jobs/Candidates list
 * until the user has seen what the AI made of each file:
 *
 *   1. PICK    — choose files. Nothing has been sent.
 *   2. START   — each file is parsed + AI-extracted server-side and the original
 *                is stored. Rows become `ready` with the extracted fields shown.
 *                NO record exists yet.
 *   3. CONFIRM — the user presses "Add N jobs"; only now are the records created
 *                (jobs land as drafts, duplicates get flagged).
 *   4. DONE    — the result card: counts, one line per record, and for jobs a
 *                Publish button (draft → active) plus a duplicate prompt on exit.
 *
 * Cancelling during stage 2 costs the AI tokens already spent but leaves the
 * workspace untouched, which is the point of the split.
 *
 * Web-only (uses the browser file picker).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, ScrollView, Platform, ActivityIndicator, Pressable } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Modal, ActionButton, Icon, StatTile } from '../ui';
import { showToast } from '../toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { web } from '../../utils/web';
import {
  extractJdFile, createRequisition, activateRequisition, closeRequisition,
} from '../../services/hire/requisitionV2';
import { extractResumeFile, createCandidate, lookupCandidateByContact } from '../../services/hire/candidateV2';
import type { ExtractFileResult } from '../../schemas/hire/requisitionV2';
import type { ExtractCandidateFileResult } from '../../schemas/hire/candidateV2';

export type BulkKind = 'job' | 'candidate';

const UPLOAD_ACCEPT =
  '.pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Server accepts a 25mb JSON body; base64 inflates bytes ~33%, so cap the raw
 *  file below that and reject oversize files up front with a clear reason
 *  instead of spending an upload on a guaranteed 413. */
const MAX_FILE_BYTES = 18 * 1024 * 1024;

/** How many files are in flight at once. Above ~3 the AI extraction step starts
 *  queueing server-side anyway, and SK-Meter usage gets spiky. */
const CONCURRENCY = 3;

/** 'ready' = read by AI, nothing created yet — the review state. */
type RowStatus = 'queued' | 'processing' | 'ready' | 'saving' | 'created' | 'duplicate' | 'failed';

type Stage = 'pick' | 'review' | 'done';

interface Row {
  id: string;
  file: File;
  name: string;
  status: RowStatus;
  /** Headline: the extracted title/name, then the record code once created. */
  title?: string;
  /** The few extracted fields worth eyeballing, pre-joined for display. */
  facts?: string;
  /** Failure reason (failed rows only). */
  error?: string;
  /** Extraction output, held between START and CONFIRM. */
  jd?: ExtractFileResult;
  cv?: ExtractCandidateFileResult;
  /** Candidates only — set at review time when this contact is already on file.
   *  Adding is still allowed; the new record gets flagged as a possible dup. */
  existsAs?: { code: string; name: string; status: string };
  /** Excluded from the write (the user chose "skip the ones already on file"). */
  skipped?: boolean;
  /** How this row is a duplicate:
   *   'flagged'  — the record WAS created and carries an advisory duplicate flag
   *   'existing' — nothing was created; the contact already belongs to someone */
  dupKind?: 'flagged' | 'existing';
  /** Id of the created record — needed to publish/close straight from the card. */
  recordId?: string;
  /** Set once this job has been activated (draft → active) from the card. */
  published?: boolean;
}

const KIND_LABEL: Record<BulkKind, { one: string; many: string; section: string }> = {
  job: { one: 'job', many: 'jobs', section: 'Jobs' },
  candidate: { one: 'candidate', many: 'candidates', section: 'Candidates' },
};

/** Read a browser File as bare base64 (strips the data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** File name without its extension — used as a title/name fallback. */
function baseName(filename: string): string {
  return filename.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
}

/** Machine-readable code from the API error envelope `{ error: { code } }`. */
function apiErrorCode(e: unknown): string | undefined {
  return (e as any)?.response?.data?.error?.code;
}

export default function BulkUploadModal({
  open, kind, onClose, onDone,
}: {
  open: boolean;
  kind: BulkKind;
  onClose: () => void;
  /** Fired once records have been created, so the caller can refresh lists. */
  onDone?: (summary: { created: number; duplicate: number; failed: number }) => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, font } = theme;
  const L = KIND_LABEL[kind];

  const [rows, setRows] = useState<Row[]>([]);
  const [stage, setStage] = useState<Stage>('pick');
  const [busy, setBusy] = useState(false);      // reading files (stage 2)
  const [saving, setSaving] = useState(false);  // creating records (stage 3)
  const [publishing, setPublishing] = useState(false);
  // Set when Done is pressed with duplicates still on the card — the user has to
  // say whether the repeats stay in the list or get closed again.
  const [dupPrompt, setDupPrompt] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  // The "are you sure" step before anything is written to the workspace.
  const [addPrompt, setAddPrompt] = useState(false);

  const queuedCount = rows.filter((r) => r.status === 'queued').length;
  const readyCount = rows.filter((r) => r.status === 'ready').length;
  const summary = useMemo(
    () => ({
      created: rows.filter((r) => r.status === 'created').length,
      duplicate: rows.filter((r) => r.status === 'duplicate').length,
      failed: rows.filter((r) => r.status === 'failed').length,
    }),
    [rows],
  );
  const landed = useMemo(() => rows.filter((r) => r.status === 'created' || r.status === 'duplicate'), [rows]);
  const failures = useMemo(() => rows.filter((r) => r.status === 'failed'), [rows]);

  /** Jobs land as drafts; these are the ones the card can still publish. */
  const publishable = useMemo(
    () => rows.filter((r) => kind === 'job' && r.status === 'created' && !!r.recordId && !r.published),
    [rows, kind],
  );
  /** Every duplicate this run produced, either kind. */
  const dupRows = useMemo(() => rows.filter((r) => r.status === 'duplicate'), [rows]);
  /** Duplicates that were actually CREATED, so they can be closed again. Only
   *  jobs qualify: a taken contact means no candidate row exists to remove. */
  const removableDups = useMemo(
    () => dupRows.filter((r) => kind === 'job' && r.dupKind === 'flagged' && !!r.recordId),
    [dupRows, kind],
  );
  const skippedDups = useMemo(() => dupRows.filter((r) => r.dupKind === 'existing'), [dupRows]);
  /** Ready rows whose contact is already on file — the "add anyway?" cases. */
  const existingReady = useMemo(() => rows.filter((r) => r.status === 'ready' && !!r.existsAs), [rows]);

  const reset = () => {
    setRows([]); setStage('pick'); setBusy(false); setSaving(false);
    setPublishing(false); setDupPrompt(false); setDiscarding(false); setAddPrompt(false);
  };
  const finish = () => { reset(); onClose(); };

  /**
   * Cancel / Done / X. A finished run that created repeats asks first — the
   * duplicates are real rows in the Jobs list, and leaving without a decision is
   * how you end up with the same job three times over. Cancelling from `review`
   * needs no prompt: nothing was created.
   */
  const close = () => {
    if (busy || saving || publishing) return;
    if (dupRows.length) { setDupPrompt(true); return; }
    finish();
  };

  // Open the browser multi-file picker and queue the chosen files. Oversize
  // files are queued as already-failed so the reason is visible before the run.
  /** Queue chosen files. Oversize ones are queued as already-failed so the reason
   *  is visible before a run, rather than after a wasted upload. */
  const queueFiles = (picked: File[]) => {
    if (!picked.length) return;
    setRows((prev) => {
      // Adding files after a finished run starts a fresh cycle: drop the previous
      // run's landed rows, keep anything still queued or failed.
      const base = stage === 'done' ? prev.filter((r) => r.status === 'queued' || r.status === 'failed') : prev;
      return [
        ...base,
        ...picked.map((f, i) => {
          const tooBig = f.size > MAX_FILE_BYTES;
          return {
            id: `${f.name}:${f.size}:${base.length + i}`,
            file: f,
            name: f.name,
            status: (tooBig ? 'failed' : 'queued') as RowStatus,
            error: tooBig ? `File is ${(f.size / 1024 / 1024).toFixed(1)} MB — the limit is 18 MB.` : undefined,
          };
        }),
      ];
    });
    setStage('pick');
  };

  const pick = () => {
    if (Platform.OS !== 'web') { showToast('error', 'Upload on web', 'Bulk import is available in the web app.'); return; }
    const doc: any = (globalThis as any).document;
    const input = doc.createElement('input');
    input.type = 'file';
    input.accept = UPLOAD_ACCEPT;
    input.multiple = true;
    input.onchange = () => queueFiles(Array.from(input.files ?? []));
    input.click();
  };

  /**
   * Drag-and-drop on the empty zone. RN-web does not surface DOM drag events as
   * props, so the listeners are attached to the underlying node. Every handler
   * preventDefault()s — without it the browser navigates away to the dropped file,
   * which loses whatever was already queued.
   */
  const zoneRef = useRef<any>(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (Platform.OS !== 'web' || !open) return;
    const node: any = zoneRef.current;
    if (!node?.addEventListener) return;
    const stop = (e: any) => { e.preventDefault(); e.stopPropagation(); };
    const onOver = (e: any) => { stop(e); setDragging(true); };
    const onLeave = (e: any) => { stop(e); setDragging(false); };
    const onDrop = (e: any) => {
      stop(e);
      setDragging(false);
      const files: File[] = Array.from(e.dataTransfer?.files ?? []);
      // Silently ignoring an unsupported drop looks like a broken drop target.
      const ok = files.filter((f) => /\.(pdf|csv|xlsx|xls)$/i.test(f.name));
      if (ok.length !== files.length) {
        showToast('error', 'Some files were skipped', 'Only PDF, CSV and Excel files can be imported.');
      }
      queueFiles(ok);
    };
    node.addEventListener('dragenter', onOver);
    node.addEventListener('dragover', onOver);
    node.addEventListener('dragleave', onLeave);
    node.addEventListener('drop', onDrop);
    return () => {
      node.removeEventListener('dragenter', onOver);
      node.removeEventListener('dragover', onOver);
      node.removeEventListener('dragleave', onLeave);
      node.removeEventListener('drop', onDrop);
    };
  }, [open, stage]);

  const patch = (id: string, next: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...next } : r)));

  /** Run a set of rows through `work` with a small worker pool, FIFO. */
  const pool = async (queue: Row[], work: (row: Row) => Promise<void>) => {
    let next = 0;
    const worker = async () => {
      for (;;) {
        const row = queue[next++];
        if (!row) return;
        await work(row);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
  };

  // ── Stage 2: START — read every queued file. Creates nothing. ──────────────
  const start = async () => {
    const queue = rows.filter((r) => r.status === 'queued');
    if (!queue.length) return;
    setBusy(true);
    let ready = 0;
    let failed = 0;

    await pool(queue, async (row) => {
      patch(row.id, { status: 'processing', title: undefined, facts: undefined, error: undefined });
      try {
        const contentBase64 = await fileToBase64(row.file);
        const contentType = row.file.type || 'application/octet-stream';

        if (kind === 'job') {
          const jd = await extractJdFile({ filename: row.name, contentBase64, contentType }, 'bulk_import');
          const musts = jd.mustHaveSkills ?? [];
          patch(row.id, {
            status: 'ready',
            jd,
            title: jd.title?.trim() || baseName(row.name),
            facts: [
              `${jd.openings ?? 1} opening${(jd.openings ?? 1) === 1 ? '' : 's'}`,
              musts.length ? `${musts.length} must-have: ${musts.slice(0, 3).join(', ')}${musts.length > 3 ? '…' : ''}` : 'no must-have skills found',
              jd.experienceMin != null ? `${jd.experienceMin}+ y` : '',
              (jd.locations ?? []).slice(0, 2).join(', '),
            ].filter(Boolean).join(' · '),
          });
        } else {
          const cv = await extractResumeFile({ filename: row.name, contentBase64, contentType }, 'bulk_import');
          // Caught here rather than at save time, so the review card is honest
          // about what will actually be added.
          if (!cv.email?.trim()) throw new Error('No email found in the resume — add this one manually.');
          // Is this person already on file? Asked now so the review card — and
          // the confirmation — can say so before anything is written.
          let existsAs: Row['existsAs'];
          try {
            const hit = await lookupCandidateByContact(cv.email, cv.phone);
            if (hit) existsAs = { code: hit.code, name: hit.name, status: hit.status };
          } catch { /* non-fatal: the create still flags duplicates server-side */ }
          patch(row.id, {
            status: 'ready',
            cv,
            existsAs,
            title: cv.name?.trim() || baseName(row.name),
            facts: [
              cv.email.trim(),
              cv.phone ?? '',
              existsAs ? `already on file as ${existsAs.code}${existsAs.status !== 'active' ? ` (${existsAs.status})` : ''}` : '',
            ].filter(Boolean).join(' · '),
          });
        }
        ready += 1;
      } catch (e) {
        failed += 1;
        patch(row.id, { status: 'failed', error: getApiErrorMessage(e, 'Could not read this file.') });
      }
    });

    setBusy(false);
    setStage('review');
    showToast(
      ready ? 'success' : 'error',
      ready ? `${ready} file${ready === 1 ? '' : 's'} read` : 'Could not read the files',
      ready
        ? `Review the details, then add ${ready === 1 ? 'it' : 'them'} to ${L.section}.${failed ? ` ${failed} failed.` : ''}`
        : 'Nothing could be extracted.',
    );
  };

  // ── Stage 3: CONFIRM — create the records the user just reviewed. ──────────
  const confirmAdd = async (skipExisting = false) => {
    // "Skip the ones already on file" drops those rows from the write and marks
    // them, so the result card still accounts for every file picked.
    const all = rows.filter((r) => r.status === 'ready');
    const queue = skipExisting ? all.filter((r) => !r.existsAs) : all;
    if (skipExisting) {
      all.filter((r) => r.existsAs).forEach((r) => patch(r.id, {
        status: 'duplicate', dupKind: 'existing', skipped: true,
        facts: `skipped — already on file as ${r.existsAs!.code}`,
      }));
    }
    if (!queue.length) {
      setAddPrompt(false);
      setStage('done');
      showToast('success', 'Nothing to add', 'Every file was already on file.');
      return;
    }
    setAddPrompt(false);
    setSaving(true);
    const tally = { created: 0, duplicate: 0, failed: 0 };

    await pool(queue, async (row) => {
      patch(row.id, { status: 'saving' });
      try {
        if (kind === 'job') {
          const jd = row.jd!;
          const out = await createRequisition({
            title: jd.title?.trim() || baseName(row.name),
            clientAccountId: null,
            mustHaveSkills: jd.mustHaveSkills ?? [],
            niceToHaveSkills: jd.niceToHaveSkills ?? [],
            experienceMin: jd.experienceMin ?? 0,
            experienceMax: jd.experienceMax,
            locations: jd.locations ?? [],
            remotePolicy: jd.remotePolicy ?? 'onsite',
            salaryCurrency: 'INR',
            openingsCount: jd.openings ?? 1,
            descriptionRaw: jd.descriptionRaw || null,
            sourceFileId: jd.sourceFileId,
            sourceFilename: jd.sourceFilename,
          });
          const created = out.requisition;
          const status: RowStatus = out.result === 'duplicate' ? 'duplicate' : 'created';
          patch(row.id, {
            status,
            dupKind: status === 'duplicate' ? 'flagged' : undefined,
            title: `${created.code} · ${created.revision?.title ?? created.title}`,
            facts: [row.facts, out.result === 'duplicate' && out.duplicateOfCode ? `repeat of ${out.duplicateOfCode}` : '']
              .filter(Boolean).join(' · '),
            recordId: created.id,
          });
          if (status === 'duplicate') tally.duplicate += 1; else tally.created += 1;
        } else {
          const cv = row.cv!;
          const out = await createCandidate({
            name: cv.name?.trim() || baseName(row.name),
            email: cv.email!.trim(),
            phone: cv.phone ?? null,
            source: 'upload_form',
            resumeText: cv.resumeText,
            sourceFileId: cv.sourceFileId,
            sourceFilename: cv.sourceFilename,
            consent: { purpose: 'Recruitment matching', ttlDays: 365, source: 'upload_form' },
          });
          const status: RowStatus = out.result === 'duplicate' ? 'duplicate' : 'created';
          // The profile is derived server-side from the resume at create time, so
          // these facts only exist now — same idea as the job card's facts line.
          const p = out.candidate.profile;
          const skills = p?.skills ?? [];
          patch(row.id, {
            status,
            dupKind: status === 'duplicate' ? 'flagged' : undefined,
            title: `${out.candidate.code} · ${out.candidate.name}`,
            facts: [
              out.candidate.email ?? '',
              out.candidate.phone ?? '',
              p?.totalExperienceYears != null ? `${p.totalExperienceYears} y exp` : '',
              p?.currentLocation ?? '',
              // The create now returns before the résumé has been read, so an
              // absent profile here means "not yet", not "nothing found". Saying
              // "no skills extracted" for a résumé that is mid-extraction would
              // read as a failed import of a perfectly good file.
              skills.length
                ? `${skills.length} skills: ${skills.slice(0, 3).map((s) => s.slug).join(', ')}${skills.length > 3 ? '…' : ''}`
                : (out.candidate.profilePending ? 'résumé being read — skills appear on the profile shortly' : 'no skills extracted'),
              status === 'duplicate' && out.candidate.duplicateOf ? `repeat of ${out.candidate.duplicateOf.code}` : '',
            ].filter(Boolean).join(' · '),
            recordId: out.candidate.id,
          });
          if (status === 'duplicate') tally.duplicate += 1; else tally.created += 1;
        }
      } catch (e) {
        const msg = getApiErrorMessage(e, `Could not add this ${L.one}.`);
        // The contact-already-taken conflict is a DUPLICATE, not a failure: the
        // person is already in the pool, so nothing was created and nothing is
        // broken. Counting it as failed made a re-import look like 10 errors.
        if (apiErrorCode(e) === 'CANDIDATE_CONTACT_TAKEN') {
          tally.duplicate += 1;
          patch(row.id, {
            status: 'duplicate',
            dupKind: 'existing',
            title: row.title ?? row.name,
            facts: `already in your pool as ${msg.match(/CAND-[A-Z0-9]+/)?.[0] ?? 'an existing candidate'} — not added again`,
          });
          return;
        }
        tally.failed += 1;
        patch(row.id, { status: 'failed', error: msg });
      }
    });

    setSaving(false);
    setStage('done');
    const parts = [
      tally.created ? `${tally.created} ${tally.created === 1 ? L.one : L.many} added` : '',
      tally.duplicate ? `${tally.duplicate} possible duplicate${tally.duplicate === 1 ? '' : 's'}` : '',
      tally.failed ? `${tally.failed} failed` : '',
    ].filter(Boolean);
    showToast(
      tally.failed && !tally.created && !tally.duplicate ? 'error' : 'success',
      `Added to ${L.section}`,
      parts.join(' · ') || 'Nothing to add.',
    );
    onDone?.(tally); // now — and only now — the list should refresh
  };

  /**
   * Publish every job the run created — draft → active in one press. Activation
   * is capped by the plan's open-opening allowance server-side, so this can
   * partially succeed: each job is tracked on its own and the ones that were
   * refused keep their reason on the row.
   */
  const publishAll = async () => {
    const targets = publishable;
    if (!targets.length) return;
    setPublishing(true);
    let ok = 0;
    let firstError = '';
    for (const row of targets) {
      try {
        await activateRequisition(row.recordId!);
        patch(row.id, { published: true });
        ok += 1;
      } catch (e) {
        const msg = getApiErrorMessage(e, 'Could not publish this job.');
        if (!firstError) firstError = msg;
        patch(row.id, { error: msg });
      }
    }
    setPublishing(false);
    const refused = targets.length - ok;
    showToast(
      ok ? 'success' : 'error',
      ok ? `${ok} job${ok === 1 ? '' : 's'} published` : 'Could not publish',
      refused ? `${refused} could not be activated — ${firstError}` : 'They are active and ready to match.',
    );
    onDone?.(summary);
  };

  /** "Remove them" → close (archive) each duplicate this run created. */
  const discardDuplicates = async () => {
    setDiscarding(true);
    let removed = 0;
    let firstError = '';
    for (const row of removableDups) {
      try { await closeRequisition(row.recordId!); removed += 1; } catch (e) {
        if (!firstError) firstError = getApiErrorMessage(e, 'Could not remove this one.');
      }
    }
    setDiscarding(false);
    const failed = removableDups.length - removed;
    showToast(
      removed ? 'success' : 'error',
      removed ? `${removed} duplicate${removed === 1 ? '' : 's'} removed` : 'Could not remove the duplicates',
      failed ? `${failed} could not be closed — ${firstError}` : 'The originals are untouched.',
    );
    onDone?.(summary);
    finish();
  };

  /** Re-queue the failed rows (minus the ones that can never succeed, i.e.
   *  oversize) so a transient extraction error can be retried on its own. */
  const retryFailed = () => {
    setRows((prev) => prev.map((r) =>
      r.status === 'failed' && r.file.size <= MAX_FILE_BYTES
        ? { ...r, status: 'queued' as RowStatus, error: undefined }
        : r));
    setStage('pick');
  };
  const retryableCount = failures.filter((r) => r.file.size <= MAX_FILE_BYTES).length;

  const statusMeta = (s: RowStatus): { icon: React.ComponentProps<typeof Icon>['name']; color: string; label: string } => {
    switch (s) {
      case 'created': return { icon: 'check-circle', color: colors.success, label: 'Added' };
      case 'duplicate': return { icon: 'dup', color: colors.warn, label: 'Duplicate' };
      case 'failed': return { icon: 'alert-circle', color: colors.danger, label: 'Failed' };
      case 'processing': return { icon: 'refresh', color: colors.accent, label: 'Reading…' };
      case 'saving': return { icon: 'refresh', color: colors.accent, label: 'Adding…' };
      case 'ready': return { icon: 'check', color: colors.ink2, label: 'Ready' };
      default: return { icon: 'import', color: colors.ink4, label: 'Queued' };
    }
  };

  const doneCount = rows.filter((r) => r.status !== 'queued' && r.status !== 'processing').length;
  // Progress for the batch as a whole. Reading counts rows that have been read;
  // adding counts rows that have landed — two phases, one ring.
  const savedCount = rows.filter((r) => r.status === 'created' || r.status === 'duplicate' || r.status === 'failed').length;
  const phaseDone = saving ? savedCount : doneCount;
  const phaseTotal = saving ? rows.filter((r) => r.status !== 'queued').length : rows.length;
  // Files being worked on right now. The first file can take 10–20s to come back
  // from AI extraction, so a ring showing a flat 0/N reads as a hang. In-flight
  // rows earn half credit and the ring keeps sweeping — the run always looks live.
  const inFlight = rows.filter((r) => r.status === (saving ? 'saving' : 'processing')).length;

  // Prompts are drawn INSIDE the dialog card (not a second RN Modal — that
  // renders underneath and the user never sees it).
  const overlayNode = addPrompt ? (
    // The write confirmation: last stop before anything enters the workspace.
    <OverlayScrim>
      <OverlayCard accent={colors.accent} onClose={saving ? undefined : () => setAddPrompt(false)}>
        <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accentSoft, alignSelf: 'center' }}>
          <Icon name={kind === 'job' ? 'layers' : 'users'} size={24} color={colors.ink} />
        </View>
        <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center' }}>
          Add {readyCount} {readyCount === 1 ? L.one : L.many} to {L.section}?
        </Text>
        <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textAlign: 'center', lineHeight: 20 }}>
          {kind === 'job'
            ? `${readyCount === 1 ? 'This job' : 'These jobs'} will be created as ${readyCount === 1 ? 'a draft' : 'drafts'} — nothing goes live until you publish. `
              + `Anything that repeats a live job's title is flagged so you can decide afterwards.`
            : `${readyCount === 1 ? 'This candidate' : 'These candidates'} will be added to your pool with consent recorded for recruitment matching.`}
        </Text>

        {/* The "add the existing one anyway?" decision, named explicitly. */}
        {existingReady.length > 0 && (
          <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, backgroundColor: colors.warnSoft, padding: 12, gap: 6 }}>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold }}>
              {existingReady.length} of these {existingReady.length === 1 ? 'is' : 'are'} already on file
            </Text>
            <Text style={{ fontSize: fontSize.xs, color: colors.ink2, lineHeight: 17 }}>
              {existingReady.slice(0, 4).map((r) => `${r.title} → ${r.existsAs!.code}`).join('\n')}
              {existingReady.length > 4 ? `\n…and ${existingReady.length - 4} more` : ''}
            </Text>
            <Text style={{ fontSize: fontSize.xs, color: colors.ink2, lineHeight: 17 }}>
              Adding {existingReady.length === 1 ? 'it' : 'them'} anyway creates a second record, flagged as a possible
              duplicate on the profile so you can merge or dismiss it later. Nothing is overwritten.
            </Text>
          </View>
        )}

        <View style={{ gap: 8, marginTop: 6 }}>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1 }}>
              <ActionButton label="Go back" variant="secondary" onPress={() => setAddPrompt(false)} disabled={saving} />
            </View>
            <View style={{ flex: 1 }}>
              <ActionButton
                label={existingReady.length ? `Add all ${readyCount}` : `Add ${readyCount}`}
                variant="primary"
                icon="check"
                loading={saving}
                onPress={() => confirmAdd(false)}
              />
            </View>
          </View>
          {existingReady.length > 0 && existingReady.length < readyCount && (
            <ActionButton
              label={`Add only the ${readyCount - existingReady.length} new — skip the rest`}
              variant="secondary"
              onPress={() => confirmAdd(true)}
              disabled={saving}
            />
          )}
        </View>
      </OverlayCard>
    </OverlayScrim>
  ) : dupPrompt ? (
    <OverlayScrim>
      {/* X dismisses the prompt and goes back to the result card — it does not
          decide anything, so the duplicates stay until Keep/Remove is chosen. */}
      <OverlayCard accent={colors.warn} onClose={discarding ? undefined : () => setDupPrompt(false)}>
        <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.warnSoft, alignSelf: 'center' }}>
          <Icon name="dup" size={24} color={colors.warn} />
        </View>
        <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center' }}>
          {dupRows.length} {dupRows.length === 1 ? 'duplicate' : 'duplicates'} in this import
        </Text>
        <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textAlign: 'center', lineHeight: 20 }}>
          {/* Two genuinely different outcomes, so say which one happened. */}
          {skippedDups.length > 0 && (
            `${skippedDups.length === 1 ? 'One file' : `${skippedDups.length} files`} ${skippedDups.length === 1 ? 'belongs' : 'belong'} to `
            + `${skippedDups.length === 1 ? 'someone' : 'people'} already in your pool, so nothing was added for `
            + `${skippedDups.length === 1 ? 'it' : 'them'} — the existing ${skippedDups.length === 1 ? 'record keeps' : 'records keep'} its history. `
          )}
          {removableDups.length > 0 && (
            `${removableDups.length === 1 ? 'One job' : `${removableDups.length} jobs`} `
            + `${removableDups.length === 1 ? 'matches a title that already has' : 'match titles that already have'} a live job, and `
            + `${removableDups.length === 1 ? 'it is' : 'they are'} now sitting in your list as `
            + `${removableDups.length === 1 ? 'a draft' : 'drafts'}. Remove ${removableDups.length === 1 ? 'it' : 'them'} to keep the list clean, `
            + `or keep ${removableDups.length === 1 ? 'it' : 'them'} if `
            + `${removableDups.length === 1 ? 'this is a genuinely separate opening' : 'these are genuinely separate openings'}. `
            + `Removing only closes the copies — nothing is deleted.`
          )}
        </Text>
        <View style={{ flexDirection: 'row', gap: 10, marginTop: 6 }}>
          {removableDups.length > 0 ? (
            <>
              <View style={{ flex: 1 }}>
                <ActionButton label="Keep them" variant="secondary" onPress={() => { setDupPrompt(false); finish(); }} disabled={discarding} />
              </View>
              <View style={{ flex: 1 }}>
                <ActionButton label={`Remove ${removableDups.length === 1 ? 'it' : 'them'}`} variant="primary" loading={discarding} onPress={discardDuplicates} />
              </View>
            </>
          ) : (
            // Nothing was created, so there is nothing to remove — acknowledge only.
            <View style={{ flex: 1 }}>
              <ActionButton label="Got it" variant="primary" onPress={() => { setDupPrompt(false); finish(); }} />
            </View>
          )}
        </View>
      </OverlayCard>
    </OverlayScrim>
  ) : undefined;

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Bulk import · ${L.section}`}
      size="wide"
      overlay={overlayNode}
      footer={
        // Cancel/Done sits on the LEFT, apart from the forward actions on the
        // right, so "leave" is never adjacent to "commit".
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <ActionButton
            label={stage === 'done' ? 'Done' : 'Cancel'}
            variant="secondary"
            onPress={close}
            disabled={busy || saving || publishing}
          />
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {stage !== 'pick' && retryableCount > 0 && (
              <ActionButton label={`Retry ${retryableCount} failed`} variant="secondary" icon="refresh" onPress={retryFailed} disabled={busy || saving} />
            )}
            {/* The empty state already has "Choose files" as its focal action —
                a second one in the footer competes with it for the same job. */}
            {rows.length > 0 && (
              <ActionButton
                label={stage === 'done' ? 'Import more' : 'Add files'}
                variant="secondary"
                icon="upload"
                onPress={pick}
                disabled={busy || saving}
              />
            )}
            {/* Stage 2 trigger — reads the files, creates nothing. */}
            {(queuedCount > 0 || busy) && (
              <ActionButton
                label={`Start · ${queuedCount} file${queuedCount === 1 ? '' : 's'}`}
                variant="primary"
                icon="play"
                onPress={start}
                loading={busy}
                disabled={!queuedCount}
              />
            )}
            {/* Stage 3 trigger — opens the write confirmation. */}
            {stage === 'review' && readyCount > 0 && (
              <ActionButton
                label={`Add ${readyCount} ${readyCount === 1 ? L.one : L.many}`}
                variant="primary"
                icon="check"
                onPress={() => setAddPrompt(true)}
                loading={saving}
              />
            )}
          </View>
        </View>
      }
    >
      <View style={{ gap: 14 }}>
        {/* ── Stage 4: the single result card ─────────────────────────────── */}
        {stage === 'done' ? (
          <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, backgroundColor: colors.card, overflow: 'hidden' }}>
            <View style={{ padding: 14, gap: 12, borderBottomWidth: 1, borderBottomColor: colors.line }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <Text style={{ flex: 1, minWidth: 0, fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold }}>
                  Added to {L.section}
                </Text>
                {/* Imported jobs land as drafts — publish them all in one press. */}
                {publishable.length > 0 && (
                  <ActionButton
                    label={`Publish ${publishable.length} job${publishable.length === 1 ? '' : 's'}`}
                    variant="primary"
                    icon="check"
                    size="sm"
                    loading={publishing}
                    onPress={publishAll}
                  />
                )}
              </View>
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <StatTile label={`${L.section} added`} value={summary.created} tone="success" />
                <StatTile label="Duplicates" value={summary.duplicate} tone={summary.duplicate ? 'warn' : undefined} />
                <StatTile label="Failed" value={summary.failed} tone={summary.failed ? 'danger' : undefined} />
              </View>
            </View>

            <ScrollView style={{ maxHeight: 300 }}>
              {landed.map((r) => {
                const m = statusMeta(r.status);
                return (
                  <View key={r.id} style={{ flexDirection: 'row', gap: 10, paddingHorizontal: 14, paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: colors.line }}>
                    <Icon name={m.icon} size={16} color={m.color} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium }} numberOfLines={1}>
                        {r.title ?? r.name}
                      </Text>
                      {!!r.facts && (
                        <Text style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 3 }} numberOfLines={2}>{r.facts}</Text>
                      )}
                      {/* A job refused by the plan's opening cap keeps its reason. */}
                      {!!r.error && (
                        <Text style={{ fontSize: fontSize.xs, color: colors.danger, marginTop: 3 }} numberOfLines={2}>{r.error}</Text>
                      )}
                    </View>
                    {r.status === 'duplicate' ? (
                      <Text style={{ fontSize: fontSize.xs, color: colors.warn, fontWeight: fontWeight.semibold }}>Duplicate</Text>
                    ) : kind === 'job' ? (
                      <Text style={{ fontSize: fontSize.xs, color: r.published ? colors.success : colors.ink4, fontWeight: fontWeight.semibold }}>
                        {r.published ? 'Active' : 'Draft'}
                      </Text>
                    ) : null}
                  </View>
                );
              })}

              {failures.map((r) => (
                <View key={r.id} style={{ flexDirection: 'row', gap: 10, paddingHorizontal: 14, paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: colors.line }}>
                  <Icon name="alert-circle" size={16} color={colors.danger} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium }} numberOfLines={1}>{r.name}</Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.danger, marginTop: 3 }} numberOfLines={2}>{r.error}</Text>
                  </View>
                </View>
              ))}
            </ScrollView>
          </View>
        ) : (
          <>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, lineHeight: 19 }}>
              {stage === 'review'
                ? `Read by AI — nothing has been added yet. Check the details below, then press “Add ${readyCount} ${readyCount === 1 ? L.one : L.many}” to put ${readyCount === 1 ? 'it' : 'them'} in ${L.section}. Cancel and nothing is created.`
                : `Choose several PDF, CSV or Excel files, then press Start. Each file is read by AI and the fields are extracted — the original document is kept on file. Nothing is added to ${L.section} until you confirm.${kind === 'candidate' ? ' Resumes without an email are skipped.' : ''}`}
            </Text>

            {/* Review counts, once the files have been read. */}
            {stage === 'review' && (
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <StatTile label="Ready to add" value={readyCount} tone={readyCount ? 'success' : undefined} />
                <StatTile label="Failed" value={failures.length} tone={failures.length ? 'danger' : undefined} />
              </View>
            )}

            {rows.length === 0 ? (
              <View
                ref={zoneRef}
                style={[
                  {
                    alignItems: 'center', justifyContent: 'center', gap: 4,
                    paddingVertical: 40, paddingHorizontal: 24,
                    borderRadius: radii.lg, borderWidth: 1.5,
                    borderColor: dragging ? colors.ink : colors.line,
                    backgroundColor: dragging ? colors.paper3 : colors.paper2,
                  },
                  Platform.OS === 'web' ? ({
                    borderStyle: 'dashed',
                    transition: 'background 160ms ease, border-color 160ms ease, transform 160ms ease',
                    transform: dragging ? 'scale(1.004)' : 'none',
                  } as any) : {},
                ]}
              >
                {/* Layered medallion — a ring behind the glyph gives the zone a
                    focal point without adding colour to a grayscale system. */}
                <View
                  style={[
                    {
                      width: 60, height: 60, borderRadius: 30, alignItems: 'center', justifyContent: 'center',
                      backgroundColor: colors.card, borderWidth: 1,
                      borderColor: dragging ? colors.ink : colors.line, marginBottom: 12,
                    },
                    web({
                      boxShadow: dragging
                        ? '0 0 0 8px rgba(16,24,40,0.06)'
                        : '0 1px 2px rgba(16,24,40,0.05), 0 10px 22px -16px rgba(16,24,40,0.25)',
                      transition: 'box-shadow 200ms ease, border-color 160ms ease',
                    }),
                  ]}
                >
                  <Icon name="upload" size={24} color={dragging ? colors.ink : colors.ink3} />
                </View>

                <Text style={{ fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold, letterSpacing: -0.2 }}>
                  {dragging ? `Drop to add ${L.many}` : `Drag files here`}
                </Text>
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3, textAlign: 'center' }}>
                  or choose them from your computer — as many as you like
                </Text>

                <View style={{ marginTop: 14 }}>
                  <ActionButton label="Choose files" variant="primary" icon="upload" size="sm" onPress={pick} />
                </View>

                {/* States the accepted formats and the per-file ceiling up front,
                    rather than letting a rejected file teach it after the fact. */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {['PDF', 'CSV', 'XLSX', 'XLS'].map((ext) => (
                    <View
                      key={ext}
                      style={{
                        paddingVertical: 3, paddingHorizontal: 8, borderRadius: 999,
                        backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line,
                      }}
                    >
                      <Text style={{ fontFamily: font.mono, fontSize: 10, color: colors.ink3, letterSpacing: 0.3 }}>{ext}</Text>
                    </View>
                  ))}
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink4, marginLeft: 2 }}>· up to 18 MB each</Text>
                </View>
              </View>
            ) : (
              <>
                {/* One indicator for the WHOLE batch. Per-row spinners say a file
                    is busy but never how far along the run is, so a 15-file import
                    read as a list of things individually hanging. */}
                {(busy || saving) && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                    <ProgressRing done={phaseDone} total={phaseTotal} active={inFlight} spinning />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold }}>
                        {saving ? `Adding to ${L.section}…` : 'Reading files…'}
                      </Text>
                      <Text style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 2, fontFamily: font.mono }}>
                        {phaseDone} / {phaseTotal} done{inFlight ? ` · ${inFlight} in progress` : ''} · {CONCURRENCY} at a time
                      </Text>
                      {/* Say the first file is slow, so a long first wait reads as
                          expected rather than as a stall. */}
                      <Text style={{ fontSize: fontSize.xs, color: colors.ink4, marginTop: 2 }}>
                        {saving
                          ? 'Writing records — keep this open until it finishes.'
                          : 'AI reads each file end to end, so each one takes a few seconds. Keep this open.'}
                      </Text>
                    </View>
                  </View>
                )}
                <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, overflow: 'hidden' }}>
                  <ScrollView style={{ maxHeight: 320 }}>
                    {rows.map((r, i) => {
                      const m = statusMeta(r.status);
                      const working = r.status === 'processing' || r.status === 'saving';
                      return (
                        <View
                          key={r.id}
                          style={{
                            flexDirection: 'row', alignItems: 'center', gap: 10,
                            paddingHorizontal: 12, paddingVertical: 10,
                            borderBottomWidth: i === rows.length - 1 ? 0 : 1, borderBottomColor: colors.line,
                          }}
                        >
                          {working
                            ? <ActivityIndicator size="small" color={colors.accent} />
                            : <Icon name={m.icon} size={16} color={m.color} />}
                          <View style={{ flex: 1, minWidth: 0 }}>
                            {/* Once read, the extracted title leads and the file
                                name drops to the sub-line — that is what the user
                                is actually being asked to approve. */}
                            <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium }} numberOfLines={1}>
                              {r.status === 'ready' && r.title ? r.title : r.name}
                            </Text>
                            {r.status === 'ready' ? (
                              <Text style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 2 }} numberOfLines={2}>
                                {r.facts}{r.facts ? ' · ' : ''}<Text style={{ fontFamily: font.mono }}>{r.name}</Text>
                              </Text>
                            ) : !!r.error && (
                              <Text style={{ fontSize: fontSize.xs, color: colors.danger, marginTop: 2 }} numberOfLines={2}>{r.error}</Text>
                            )}
                          </View>
                          <Text style={{ fontSize: fontSize.xs, color: m.color, fontWeight: fontWeight.semibold }}>{m.label}</Text>
                        </View>
                      );
                    })}
                  </ScrollView>
                </View>
              </>
            )}
          </>
        )}
      </View>
    </Modal>
  );
}

/** Full-card scrim for the in-modal prompt (no second RN Modal, which would
 *  render beneath this dialog and be invisible to the user). */
function OverlayScrim({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={[
        { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: 'rgba(8,10,14,0.55)' },
        Platform.OS === 'web' ? ({ animation: 'sk-rise 160ms ease both', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } as any) : {},
      ]}
    >
      {children}
    </View>
  );
}

/** The centered dialog card used inside an OverlayScrim. `onClose` adds the
 *  corner X, which dismisses the prompt and returns to the card behind it. */
function OverlayCard({
  accent, onClose, children,
}: { accent: string; onClose?: () => void; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, radii, shadows } = theme;
  return (
    <View
      style={[
        { width: '100%', maxWidth: 400, backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' },
        shadows.lg as object,
      ]}
    >
      <View style={{ height: 3, backgroundColor: accent }} />
      {onClose && (
        <Pressable
          onPress={onClose}
          accessibilityLabel="Close"
          hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
          style={[
            {
              position: 'absolute', top: 15, right: 14, zIndex: 2,
              width: 30, height: 30, alignItems: 'center', justifyContent: 'center',
              borderRadius: 8, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2,
            },
            Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {},
          ]}
        >
          <Icon name="x" size={13} color={colors.ink2} />
        </Pressable>
      )}
      <View style={{ padding: 20, gap: 12 }}>{children}</View>
    </View>
  );
}

/**
 * Batch progress as a filled ring.
 *
 * Drawn with a conic-gradient on web rather than SVG: react-native-svg is
 * explicitly ignored in this bundle (see the webpack config), so an SVG arc would
 * render as nothing. Native has no conic-gradient, so it falls back to a bar —
 * the number in the middle carries the meaning either way.
 *
 * `active` (rows in flight) earns half credit, and `spinning` adds a sweeping arc
 * on top of the filled one. Together they mean the ring is never motionless while
 * the batch is running — a flat "0%" for the first slow file read as a hang.
 */
function ProgressRing({
  done, total, active = 0, spinning = false,
}: { done: number; total: number; active?: number; spinning?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontWeight, font } = theme;
  // Reported number stays honest (completed only); the arc gets the half credit.
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const arcPct = total > 0 ? Math.min(100, Math.round(((done + active * 0.5) / total) * 100)) : 0;

  if (Platform.OS !== 'web') {
    return (
      <View style={{ alignItems: 'center', gap: 6 }}>
        <View style={{ width: 54, height: 6, borderRadius: 3, backgroundColor: colors.paper3, overflow: 'hidden' }}>
          <View style={{ width: `${arcPct}%`, height: '100%', backgroundColor: colors.ink }} />
        </View>
        {/* Native has no conic-gradient or CSS animation, so the liveness signal
            is a real spinner beside the bar. */}
        {spinning && <ActivityIndicator size="small" color={colors.ink} />}
      </View>
    );
  }
  return (
    <View
      style={[
        { width: 46, height: 46, borderRadius: 23, alignItems: 'center', justifyContent: 'center' },
        // The gradient IS the arc; a smaller inner disc masks the centre.
        web({
          backgroundImage: `conic-gradient(${colors.ink} ${arcPct}%, ${colors.paper3} ${arcPct}% 100%)`,
          transition: 'background-image 240ms linear',
        }),
      ]}
    >
      {/* Continuously sweeping quarter-arc, sitting just outside the fill ring. */}
      {spinning && (
        <View
          style={[
            {
              position: 'absolute', top: -3, left: -3, right: -3, bottom: -3,
              borderRadius: 26, borderWidth: 2,
              borderTopColor: colors.ink, borderRightColor: 'transparent',
              borderBottomColor: 'transparent', borderLeftColor: 'transparent',
            },
            web({ animation: 'sk-spin 900ms linear infinite' }),
          ]}
        />
      )}
      <View
        style={{
          position: 'absolute', top: 4, left: 4, right: 4, bottom: 4,
          borderRadius: 19, backgroundColor: colors.card,
        }}
      />
      <Text style={{ fontFamily: font.mono, fontSize: 11, fontWeight: fontWeight.bold, color: colors.ink }}>{pct}%</Text>
    </View>
  );
}
